import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import api from "../../api/client";
import EmployerNavBar from "../../components/EmployerNavBar";

function validatePassword(password) {
  if (password.length < 8) return "Password must be at least 8 characters";
  if (!/[A-Z]/.test(password))
    return "Password must contain at least one uppercase letter";
  if (!/\d/.test(password)) return "Password must contain at least one number";
  return null;
}

export default function EmployerSettings() {
  const navigate = useNavigate();
  const { employerUser, employerToken, clearEmployerAuth, setEmployerAuth } =
    useAuthStore();

  const [profile, setProfile] = useState({
    company_name: employerUser?.company_name ?? "",
    company_website: employerUser?.company_website ?? "",
    company_size: employerUser?.company_size ?? "",
    logo_url: employerUser?.logo_url ?? "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  const [passwords, setPasswords] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [pwError, setPwError] = useState(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    document.title = "Joborigo — Employer Settings";
  }, []);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  const [upgrading, setUpgrading] = useState(null);

  const handleUpgrade = async (plan) => {
    setUpgrading(plan);
    try {
      const { data } = await api.post(
        "/payments/employer/checkout",
        { plan },
        authConfig,
      );

      if (window.Paddle && data.transaction_id) {
        window.Paddle.Environment.set(
          import.meta.env.VITE_PADDLE_ENVIRONMENT ?? "sandbox",
        );
        window.Paddle.Initialize({
          token: import.meta.env.VITE_PADDLE_CLIENT_TOKEN,
        });
        window.Paddle.Checkout.open({
          transactionId: data.transaction_id,
          settings: {
            displayMode: "overlay",
            theme: "light",
            locale: "en",
            successUrl: `${window.location.origin}/employers/settings?upgrade=success`,
          },
        });
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    } finally {
      setUpgrading(null);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/employer/auth/logout", {}, authConfig);
    } catch {}
    clearEmployerAuth();
    navigate("/employers/login");
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const { data } = await api.patch("/employer/me", profile, authConfig);
      setEmployerAuth(employerToken, data);
      setProfileMsg({ type: "success", text: "Profile updated successfully." });
    } catch (err) {
      setProfileMsg({
        type: "error",
        text: err.response?.data?.detail ?? err.message,
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError(null);
    setPwMsg(null);
    if (passwords.new_password !== passwords.confirm_password) {
      setPwError("New passwords do not match.");
      return;
    }
    const strength = validatePassword(passwords.new_password);
    if (strength) {
      setPwError(strength);
      return;
    }
    setPwSaving(true);
    try {
      await api.post(
        "/employer/me/change-password",
        {
          current_password: passwords.current_password,
          new_password: passwords.new_password,
        },
        authConfig,
      );
      setPwMsg("Password updated successfully.");
      setPasswords({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (err) {
      setPwError(err.response?.data?.detail ?? err.message);
    } finally {
      setPwSaving(false);
    }
  };

  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";
  const sectionClass =
    "bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 mb-6";
  const COMPANY_SIZES = [
    "1-10",
    "11-50",
    "51-200",
    "201-500",
    "501-1000",
    "1000+",
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <EmployerNavBar />

      <main className="max-w-2xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold">Employer Settings</h1>
          <p className="text-gray-400 text-sm mt-1">
            Manage your company profile and account
          </p>
        </div>

        {/* Current plan */}
        <div className={sectionClass}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
                Current plan
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You are on the{" "}
                <span className="font-medium text-gray-900 dark:text-white capitalize">
                  {employerUser?.plan ?? "starter"}
                </span>{" "}
                plan.
              </p>
            </div>
            <span className="text-xs font-medium bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 px-3 py-1 rounded-full capitalize">
              {employerUser?.plan ?? "starter"}
            </span>
          </div>

          {(() => {
            const allPlans = employerUser?.is_agency
              ? [
                  { plan: "starter", label: "Starter — Free" },
                  { plan: "solo", label: "Solo — €199/mo" },
                  { plan: "standard", label: "Standard — €499/mo" },
                  { plan: "pro", label: "Pro — €1,199/mo" },
                ]
              : [
                  { plan: "starter", label: "Starter — Free" },
                  { plan: "growth", label: "Growth — €99/mo" },
                  { plan: "scale", label: "Scale — €249/mo" },
                ];

            const currentIdx = allPlans.findIndex(
              (p) => p.plan === (employerUser?.plan ?? "starter"),
            );
            const upgradeOptions = allPlans.slice(currentIdx + 1);

            if (upgradeOptions.length === 0) return null;

            return (
              <div className="flex flex-col gap-2 mt-2">
                {upgradeOptions.map((opt) => (
                  <button
                    key={opt.plan}
                    onClick={() => handleUpgrade(opt.plan)}
                    disabled={upgrading}
                    className="border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10 transition disabled:opacity-50 text-left"
                  >
                    {upgrading === opt.plan
                      ? "Loading..."
                      : `Upgrade to ${opt.label}`}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Company profile */}
        <div className={sectionClass}>
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
            Company profile
          </h2>
          <form onSubmit={handleProfileSave} className="flex flex-col gap-4">
            <div>
              <label className={labelClass}>Company name</label>
              <input
                value={profile.company_name}
                onChange={(e) =>
                  setProfile({ ...profile, company_name: e.target.value })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Company website</label>
              <input
                type="url"
                value={profile.company_website}
                onChange={(e) =>
                  setProfile({ ...profile, company_website: e.target.value })
                }
                placeholder="https://acme.com"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Company size</label>
              <select
                value={profile.company_size}
                onChange={(e) =>
                  setProfile({ ...profile, company_size: e.target.value })
                }
                className={inputClass}
              >
                <option value="">Select size...</option>
                {COMPANY_SIZES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Branding — only meaningful for agencies, but harmless to show always */}
            <div>
              <label className={labelClass}>Logo URL</label>
              <input
                type="url"
                value={profile.logo_url}
                onChange={(e) =>
                  setProfile({ ...profile, logo_url: e.target.value })
                }
                placeholder="https://your-domain.com/logo.png"
                className={inputClass}
              />
              <p className="text-xs text-gray-400 mt-1">
                {employerUser?.is_agency
                  ? "Shown on client share pages and submission links forwarded to candidates."
                  : "Shown on submission links forwarded to candidates."}
              </p>
              {profile.logo_url && (
                <div className="mt-3 p-3 bg-gray-50 dark:bg-brand-dark-bg rounded-md flex items-center gap-3">
                  <img
                    src={profile.logo_url}
                    alt="Logo preview"
                    className="w-12 h-12 object-contain rounded"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />
                  <span
                    style={{ display: "none" }}
                    className="text-xs text-red-500"
                  >
                    Logo URL doesn't load — check the link
                  </span>
                  <span className="text-xs text-gray-500">Preview</span>
                </div>
              )}
            </div>
            {profileMsg && (
              <p
                className={`text-sm ${profileMsg.type === "success" ? "text-green-600 dark:text-green-400" : "text-red-500"}`}
              >
                {profileMsg.text}
              </p>
            )}
            <button
              type="submit"
              disabled={profileSaving}
              className="bg-brand-violet text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition self-start px-6"
            >
              {profileSaving ? "Saving..." : "Save changes"}
            </button>
          </form>
        </div>

        {/* Account */}
        <div className={sectionClass}>
          <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
            Account
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Signed in as{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {employerUser?.email}
            </span>
          </p>
        </div>

        {/* Change password */}
        <div className={sectionClass}>
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
            Change password
          </h2>
          <form onSubmit={handlePasswordChange} className="flex flex-col gap-4">
            <div>
              <label className={labelClass}>Current password</label>
              <input
                type="password"
                value={passwords.current_password}
                onChange={(e) =>
                  setPasswords({
                    ...passwords,
                    current_password: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>New password</label>
              <input
                type="password"
                value={passwords.new_password}
                onChange={(e) =>
                  setPasswords({ ...passwords, new_password: e.target.value })
                }
                placeholder="Min 8 characters, one uppercase, one number"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Confirm new password</label>
              <input
                type="password"
                value={passwords.confirm_password}
                onChange={(e) =>
                  setPasswords({
                    ...passwords,
                    confirm_password: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            {pwError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
                <p className="text-red-600 dark:text-red-400 text-sm">
                  {pwError}
                </p>
              </div>
            )}
            {pwMsg && (
              <p className="text-sm text-green-600 dark:text-green-400">
                {pwMsg}
              </p>
            )}
            <button
              type="submit"
              disabled={pwSaving}
              className="bg-brand-violet text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition self-start px-6"
            >
              {pwSaving ? "Updating..." : "Update password"}
            </button>
          </form>
        </div>

        {/* Resources */}
        <div className={sectionClass}>
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
            Resources
          </h2>
          <div className="flex flex-col gap-2">
            <a
              href="/employers/api"
              target="_blank"
              className="flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-white/10 hover:border-brand-violet dark:hover:border-brand-violet-on-dark transition group"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">📘</span>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    API Documentation
                  </p>
                  <p className="text-xs text-gray-400">
                    Integration guide, endpoints, and examples
                  </p>
                </div>
              </div>
              <svg
                className="w-4 h-4 text-gray-400 group-hover:text-brand-violet dark:group-hover:text-brand-violet-on-dark transition"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
            <a
              href="mailto:support@joborigo.com"
              className="flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-white/10 hover:border-brand-violet dark:hover:border-brand-violet-on-dark transition group"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">✉️</span>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Contact support
                  </p>
                  <p className="text-xs text-gray-400">support@joborigo.com</p>
                </div>
              </div>
              <svg
                className="w-4 h-4 text-gray-400 group-hover:text-brand-violet dark:group-hover:text-brand-violet-on-dark transition"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        </div>

        {/* Danger zone */}
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-red-100 dark:border-red-900/30 p-6">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
            Danger zone
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Deleting your employer account will permanently remove all API keys,
            integrations, and sent job positions. This cannot be undone.
          </p>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="border border-red-200 dark:border-red-800 text-red-500 px-5 py-2 rounded-lg text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition"
            >
              Delete employer account
            </button>
          ) : (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <p className="text-sm text-red-700 dark:text-red-400 font-medium mb-3">
                Are you sure? This will permanently delete your account and all
                data.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await api.delete("/employer/me", authConfig);
                      clearEmployerAuth();
                      navigate("/");
                    } catch (err) {
                      alert(err.response?.data?.detail ?? err.message);
                    }
                  }}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg text-sm font-medium transition"
                >
                  Yes, delete account
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
